import React, {
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    View,
    FlatList,
    useWindowDimensions,
    Platform,
    TouchableOpacity,
    Text,
    StyleSheet,
    StatusBar,
    Animated,
    NativeSyntheticEvent,
    NativeScrollEvent,
    Modal,
    Pressable,
} from 'react-native'
import { SafeKeyboardControllerAvoidingView } from '../../../SafeKeyboardProvider'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { LinearGradient } from 'expo-linear-gradient'
import { useNavigation, useRoute, useIsFocused } from '@react-navigation/native'
import VideoPlayerAV from '../customUtils/VideoPlayer/expo-av'
import { usePostContext } from './context/posts.context'
import { Feather, Ionicons } from '@expo/vector-icons'
import theme from '../../../theme'
import { debug, warn } from '../../utils/logger'
import InteractionsPosts, { ReactionItem } from './InteractionsPosts'
import PostWithComments from './PostWithComments'
import { useInteractionsStore } from '../../redux/zustand/interactionsPostStore'
import { TrackingServices } from '../../../database/tracking.database.js'
import { getAuthToken } from '../../utils/functions.utils'
import { update_favourite_local_posts } from '../../db/posts/index.js'
import { createAction, enqueue } from '../../services/offline/offlineQueue'
import { getConection } from '../appBar/context/HeaderContext'
import {
    request_add_favourite,
    request_add_like_wall,
} from '../../services/posts.services'
import { SafeBottomSpace } from '../customUtils/SafeBottomSpace'
import { BookmarkIcon, BookmarkSimple, HeartIcon } from 'phosphor-react-native'
import { getReactionAsset } from './utils/reactionAssets'
import { Image } from 'expo-image'
import { Video } from 'expo-av'
// Tipado mínimo de post para filtrar videos
interface MediaItem {
    media_type?: string
    content_type?: string
    media_url?: string
    media?: string
}
interface PostMinimal {
    _id: string | number
    medias?: MediaItem[]
}

interface RouteParams {
    initialPostId: string | number
}

export const ReelsViewer: React.FC = () => {
    const navigation = useNavigation<any>()
    const route = useRoute()
    const isFocused = useIsFocused()
    const { width, height } = useWindowDimensions()
    const { initialPostId } = (route.params || {}) as RouteParams
    const flatRef = useRef<FlatList>(null)
    const fadeAnim = useRef(new Animated.Value(1)).current
    const UP_NEXT_SECONDS = (theme as any)?.constants?.reelsUpNextSeconds ?? 5
    const [upNext, setUpNext] = useState<{
        visible: boolean
        seconds: number
        nextIndex: number | null
    }>({ visible: false, seconds: 0, nextIndex: null })
    const upNextTimerRef = useRef<any>(null)
    const [autoplayEnabled, setAutoplayEnabled] = useState<boolean>(true)
    const [toastMsg, setToastMsg] = useState<string>('')
    const toastOpacity = useRef(new Animated.Value(0)).current
    const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false)
    const [commentsPost, setCommentsPost] = useState<any>(null)
    const { updateFavouritePostsContext, reloadFavourite, postsContext } =
        usePostContext()
    const { isConnected, hasInternet, connectedStable } = getConection()

    // Store de interacciones
    const setInteractions = useInteractionsStore((state) => state.setInteractions)
    const toggleReaction = useInteractionsStore((state) => state.toggleReaction)
    // Estados para reactions
    const [reactions, setReactions] = useState<any[]>([])
    const [showReactionPicker, setShowReactionPicker] = useState<boolean>(false)
    const [selectedPostForReaction, setSelectedPostForReaction] = useState<any>(null)

    // Cargar reacciones disponibles al montar el componente
    useEffect(() => {
        const loadReactions = async () => {
            try {
                const { PostsServices } = require('../../../database/posts.database')
                const allReactions = await PostsServices.getAllReactions()
                if (Array.isArray(allReactions) && allReactions.length > 0) {
                    setReactions(allReactions)
                    debug(
                        'Reactions',
                        '[ReelsViewer] Loaded reactions',
                        allReactions.length,
                    )
                }
            } catch (e) {
                warn(
                    'Reactions',
                    '[ReelsViewer] Error loading reactions',
                    (e as any)?.message,
                )
            }
        }
        loadReactions()
        return () => {
            setReactions([])
        }
    }, [])

    // Handlers para las interacciones
    const handlePresentCommentsModal = (post: any) => {
        setCommentsPost(post)
        setShowCommentsModal(true)
    }

    const handleCloseCommentsModal = () => {
        setShowCommentsModal(false)
        setCommentsPost(null)
    }

    const handleUpdateInteractions = (newInteractions: any) => {
        try {
            setInteractions({ [String(newInteractions.post_id)]: newInteractions })
        } catch (e) {
            debug(
                'Interactions',
                '[ReelsViewer] Error updating interactions',
                (e as any)?.message,
            )
        }
    }

    // Handlers para interacciones individuales
    const handleLikePress = async (item: any) => {
        try {
            if (!reactions || reactions.length === 0) return

            const defaultReaction =
                reactions.find((r: any) => r.name === 'heart') ||
                reactions.find((r: any) => r.name === 'like') ||
                reactions[0]

            if (!defaultReaction) return

            // 1. Delega toda la lógica compleja de UI al store (¡Actualización optimista instantánea!)
            const { current, previous } = toggleReaction(
                String(item._id),
                defaultReaction._id || defaultReaction.id,
                reactions,
            )
            const authToken = await getAuthToken()
            if (!authToken) {
                toggleReaction(
                    String(item._id),
                    defaultReaction._id || defaultReaction.id,
                    reactions, // El catálogo que ya tienes cargado en el estado
                )
                return
            }
            let reactionID = current?.user_reaction?.[0].reaction_id ?? ''
            let data = await request_add_like_wall({
                authToken,
                post_id: item._id,
                reaction_id: reactionID,
            })

            if (data.status != 1) {
                toggleReaction(
                    String(item._id),
                    defaultReaction._id || defaultReaction.id,
                    reactions, // El catálogo que ya tienes cargado en el estado
                )
                return
            }
            // 2. Llamada al backend
            const { PostsServices } = require('../../../database/posts.database')

            // Verificamos si en el estado 'previous' ya estaba likeado para saber qué endpoint llamar
            const wasLiked = previous?.user_reaction?.some(
                (ur: any) =>
                    ur.reaction_id === (defaultReaction._id || defaultReaction.id),
            )
        } catch (e) {
            console.error('[ReelsViewer] Error in like press', e)
        }
    }

    const handleFavoritePress = async (postID: string) => {
        try {
            const test = false
            if (test) {
                console.log(
                    '[ReelsViewer] No internet connection; skipping favorite request',
                )
                return
            }

            // Obtener estado actual de favorito Y las interacciones existentes
            const currentPost = videoPosts.find((p) => p._id === postID)
            const currentFavorite = getUserFavoritePost(currentPost)
            const newFavoriteStatus = !currentFavorite
            const newFavoriteNum = newFavoriteStatus ? 1 : 0

            // Obtener interacciones actuales del store para hacer merge
            const currentStoreInteractions =
                useInteractionsStore.getState().interactions?.[String(postID)] || {}

            // Actualizar UI inmediatamente (optimistic update)
            const tempInteraction = {
                ...currentStoreInteractions,
                post_id: String(postID),
                favourite: newFavoriteNum,
                status_favourite: newFavoriteNum,
            }

            debug(
                'Interactions',
                `[ReelsViewer] Updating favorite for ${postID}: ${newFavoriteStatus}`,
            )
            setInteractions({ [String(postID)]: tempInteraction })

            if (connectedStable) {
                TrackingServices.addTracking(
                    postID,
                    'add_favourite',
                    'post_favourites',
                )

                const authToken = await getAuthToken()

                if (!authToken) return

                const data = await request_add_favourite({
                    authToken,
                    post_id: String(postID),
                })

                console.log('🚀 ~ saveFavorite ~ data:', data)

                // Actualizar store con el valor del servidor - MERGE con estado actual
                const serverFavorite = data['single']['status_favourite']
                const updatedStoreInteractions =
                    useInteractionsStore.getState().interactions?.[String(postID)] ||
                    {}
                setInteractions({
                    [String(postID)]: {
                        ...updatedStoreInteractions, // Preservar like, reactions, etc.
                        post_id: String(postID),
                        favourite: serverFavorite,
                        status_favourite: serverFavorite,
                    },
                })

                // Solo actualizar BD local - el contexto se actualizará cuando vuelvas al feed
                await update_favourite_local_posts(postID, serverFavorite)
            } else {
                // Offline: guardar local y encolar
                try {
                    await update_favourite_local_posts(postID, newFavoriteStatus)
                } catch {}
                try {
                    await enqueue(
                        createAction('favourite', String(postID), {
                            status: newFavoriteStatus,
                        }),
                    )
                } catch {}

                showToast(
                    'Favorito guardado offline. Se sincronizará al reconectar.',
                )
            }
        } catch (err) {
            console.error('Error en la solicitud de favorito:', err)
            // Revertir en caso de error
            const currentPost = videoPosts.find((p) => p._id === postID)
            const originalFavorite = getUserFavoritePost(currentPost)
            const currentStoreInteractions =
                useInteractionsStore.getState().interactions?.[String(postID)] || {}
            setInteractions({
                [String(postID)]: {
                    ...currentStoreInteractions,
                    post_id: String(postID),
                    favourite: originalFavorite ? 1 : 0,
                    status_favourite: originalFavorite ? 1 : 0,
                },
            })
        }
    }

    // Helpers para obtener estado de interacciones
    const getUserLikedPost = (item: any) => {
        try {
            const postId = String(item._id)
            debug(
                'Interactions',
                `[ReelsViewer] Getting user liked status for post ${postId}`,
            )

            // Verificar en las interacciones del store global (más actualizado)
            const storeInteractions =
                useInteractionsStore.getState().interactions?.[postId]
            const storeUserReaction = storeInteractions?.user_reaction
            const storeLiked = storeInteractions?.liked

            debug(
                'Interactions',
                `[ReelsViewer] Store user_reaction for ${postId}:`,
                storeUserReaction,
            )
            debug(
                'Interactions',
                `[ReelsViewer] Store liked for ${postId}:`,
                storeLiked,
            )

            // Si hay una actualización reciente en el store (por optimistic update), usarla prioritariamente
            if (storeInteractions) {
                // Verificar campo liked simple primero (más confiable)
                if (storeLiked !== undefined) {
                    const liked = storeLiked === 1 || storeLiked === true
                    debug(
                        'Interactions',
                        `[ReelsViewer] User liked in store: ${liked}`,
                    )
                    return liked
                }

                // Luego verificar user_reaction
                if (
                    Array.isArray(storeUserReaction) &&
                    storeUserReaction.length > 0
                ) {
                    debug(
                        'Interactions',
                        `[ReelsViewer] User has store reaction: true`,
                    )
                    return true
                }
            }

            // También verificar en las interacciones del item
            const itemInteractions = item?.interactions
            const itemUserReaction =
                itemInteractions?.user_reaction || item?.user_reaction

            debug(
                'Interactions',
                `[ReelsViewer] Item user_reaction for ${postId}:`,
                itemUserReaction,
            )

            if (Array.isArray(itemUserReaction) && itemUserReaction.length > 0) {
                debug('Interactions', `[ReelsViewer] User has item reaction: true`)
                return true // El usuario tiene una reacción en los datos del item
            }

            // Verificar en los datos directos del post (formato legacy)
            const directLiked = item?.liked
            debug(
                'Interactions',
                `[ReelsViewer] Direct liked field for ${postId}:`,
                directLiked,
            )

            if (directLiked === 1 || directLiked === true) {
                debug('Interactions', `[ReelsViewer] User liked (legacy): true`)
                return true
            }

            debug('Interactions', `[ReelsViewer] User liked status: false`)
            return false
        } catch (e) {
            debug(
                'Interactions',
                `[ReelsViewer] Error getting user liked status: ${(e as any)?.message}`,
            )
            return false
        }
    }

    const getReactionCount = (item: any) => {
        try {
            const postId = String(item._id)
            debug(
                'Interactions',
                `[ReelsViewer] Getting reaction count for post ${postId}`,
            )

            // Verificar en las interacciones del store global primero
            const storeInteractions =
                useInteractionsStore.getState().interactions?.[postId]
            const storeReactionsTotal = storeInteractions?.reactions_total

            debug(
                'Interactions',
                `[ReelsViewer] Store reactions_total for ${postId}:`,
                storeReactionsTotal,
            )

            // Usar campo simple reactions_total si está disponible
            if (
                storeReactionsTotal !== undefined &&
                Number(storeReactionsTotal) >= 0
            ) {
                const count = Number(storeReactionsTotal)
                debug(
                    'Interactions',
                    `[ReelsViewer] Store reaction count (simple): ${count}`,
                )
                return count
            }

            // Fallback a reactions array si existe
            const storeReactions = storeInteractions?.reactions
            if (Array.isArray(storeReactions) && storeReactions.length > 0) {
                const count = storeReactions.reduce(
                    (total: number, reaction: any) => {
                        return total + (Number(reaction.count) || 0)
                    },
                    0,
                )
                debug(
                    'Interactions',
                    `[ReelsViewer] Store reaction count (array): ${count}`,
                )
                return count
            }

            // Fallback a las interacciones del item
            const itemReactions =
                item?.interactions?.reactions ||
                item?.reaction_counter ||
                item?.reactions
            debug(
                'Interactions',
                `[ReelsViewer] Item reactions for ${postId}:`,
                itemReactions,
            )

            if (Array.isArray(itemReactions) && itemReactions.length > 0) {
                const count = itemReactions.reduce(
                    (total: number, reaction: any) => {
                        return total + (Number(reaction.count) || 0)
                    },
                    0,
                )
                debug('Interactions', `[ReelsViewer] Item reaction count: ${count}`)
                return count
            }

            // Fallback a campos directos
            const directCount = Number(
                item?.reactions_total || item?.reaction_count || 0,
            )
            debug(
                'Interactions',
                `[ReelsViewer] Direct reaction count: ${directCount}`,
            )
            return directCount
        } catch (e) {
            debug(
                'Interactions',
                `[ReelsViewer] Error getting reaction count: ${(e as any)?.message}`,
            )
            return 0
        }
    }

    const getUserFavoritePost = (item: any) => {
        try {
            const postId = String(item._id)
            debug(
                'Interactions',
                `[ReelsViewer] Getting user favorite status for post ${postId}`,
            )

            // Verificar en las interacciones del store global (más actualizado)
            const storeInteractions =
                useInteractionsStore.getState().interactions?.[postId]
            const storeFavorite =
                storeInteractions?.favourite || storeInteractions?.status_favourite

            debug(
                'Interactions',
                `[ReelsViewer] Store favorite for ${postId}:`,
                storeFavorite,
            )

            // Si hay una actualización reciente en el store, usarla
            if (storeInteractions && storeFavorite !== undefined) {
                const isFavorited = storeFavorite === 1 || storeFavorite === true
                debug(
                    'Interactions',
                    `[ReelsViewer] User favorite status from store: ${isFavorited}`,
                )
                return isFavorited
            }

            // También verificar en las interacciones del item
            const itemFavorite = item?.favourite || item?.status_favourite

            debug(
                'Interactions',
                `[ReelsViewer] Item favorite for ${postId}:`,
                itemFavorite,
            )

            if (itemFavorite === 1 || itemFavorite === true) {
                debug(
                    'Interactions',
                    `[ReelsViewer] User favorite status from item: true`,
                )
                return true
            }

            debug('Interactions', `[ReelsViewer] User favorite status: false`)
            return false
        } catch (e) {
            debug(
                'Interactions',
                `[ReelsViewer] Error getting user favorite status: ${(e as any)?.message}`,
            )
            return false
        }
    }

    const getFavoriteCount = (item: any) => {
        try {
            // Verificar en las interacciones del store global primero
            const storeInteractions =
                useInteractionsStore.getState().interactions?.[String(item._id)]
            const storeFavorites =
                storeInteractions?.favourite_count ||
                storeInteractions?.favorites_counter

            if (storeFavorites && Number(storeFavorites) > 0) {
                return Number(storeFavorites)
            }

            // Fallback a las interacciones del item
            const itemFavorites =
                item?.favourite_count ||
                item?.favorites_counter ||
                item?.favorites_total

            return Number(itemFavorites) || 0
        } catch {
            return 0
        }
    }

    const getCommentCount = (item: any) => {
        try {
            const postId = String(item._id)
            debug(
                'Interactions',
                `[ReelsViewer] Getting comment count for post ${postId}`,
            )

            // Verificar en las interacciones del store global primero
            const storeInteractions =
                useInteractionsStore.getState().interactions?.[postId]
            const storeComments =
                storeInteractions?.comment_count ||
                storeInteractions?.comments_counter

            debug(
                'Interactions',
                `[ReelsViewer] Store comment count for ${postId}:`,
                storeComments,
            )

            if (storeComments && Number(storeComments) > 0) {
                return Number(storeComments)
            }

            // Fallback a las interacciones del item
            const itemComments =
                item?.comment_count ||
                item?.comments_counter ||
                item?.interactions?.comment_count

            debug(
                'Interactions',
                `[ReelsViewer] Item comment count for ${postId}:`,
                itemComments,
            )

            return Number(itemComments) || 0
        } catch (e) {
            debug(
                'Interactions',
                `[ReelsViewer] Error getting comment count: ${(e as any)?.message}`,
            )
            return 0
        }
    }

    const getUserReactionIcon = (item: any) => {
        try {
            const postId = String(item._id)

            // Verificar si hay una reacción específica (no like) en el store
            const storeInteractions =
                useInteractionsStore.getState().interactions?.[postId]
            const storeUserReaction = storeInteractions?.user_reaction

            // Si hay una reacción del usuario en el store y tenemos el catálogo
            if (
                Array.isArray(storeUserReaction) &&
                storeUserReaction.length > 0 &&
                reactions.length > 0
            ) {
                const interactionReaction = storeUserReaction[0]
                const matchReactionById = reactions.find(
                    (r) => r._id === interactionReaction._id,
                )
                const mergedReaction = { ...matchReactionById, isSelected: true }
                const userReaction = mergedReaction
                const reactionName = userReaction.name || userReaction.reaction_name

                // Encontrar la reacción en el catálogo
                const reactionData = reactions.find((r) => r.name === reactionName)
                if (
                    reactionData &&
                    reactionName !== 'heart' &&
                    reactionName !== 'like'
                ) {
                    const reactionImage = getReactionAsset(reactionName)

                    return (
                        <Image
                            source={reactionImage}
                            style={{
                                width: 22,
                                height: 22,
                                marginBottom: 4,
                            }}
                        />
                    )
                }
            }

            const userLiked = getUserLikedPost(item)

            // Si el usuario dio like, mostrar corazón rojo
            if (userLiked) {
                return (
                    <Ionicons
                        name={'heart'}
                        size={22}
                        color='#FF3B30'
                        weight='fill'
                    />
                )
            }

            // También verificar en las interacciones del item original
            const itemUserReaction =
                item?.interactions?.user_reaction || item?.user_reaction
            if (
                Array.isArray(itemUserReaction) &&
                itemUserReaction.length > 0 &&
                reactions.length > 0
            ) {
                const userReaction = itemUserReaction[0]
                const reactionName = userReaction.name || userReaction.reaction_name

                const reactionData = reactions.find((r) => r.name === reactionName)
                if (
                    reactionData &&
                    reactionName !== 'heart' &&
                    reactionName !== 'like'
                ) {
                    const reactionImage = getReactionAsset(reactionName)

                    return (
                        <Image
                            source={reactionImage}
                            style={{
                                width: 22,
                                height: 22,
                                marginBottom: 4,
                            }}
                        />
                    )
                }
            }

            // Si no hay reacción del usuario, mostrar corazón vacío
            return <Ionicons name={'heart-outline'} size={22} color='white' />
        } catch (e) {
            debug(
                'Interactions',
                `[ReelsViewer] Error getting user reaction icon: ${(e as any)?.message}`,
            )
            return (
                <Ionicons
                    name={'heart-outline'}
                    size={22}
                    color='white'
                    weight='regular'
                />
            )
        }
    }

    const handleReactionPress = (item: any) => {
        try {
            debug('Interactions', '[ReelsViewer] Reaction press - quick like')
            // Tap rápido = like/unlike
            handleLikePress(item)
        } catch (e) {
            debug(
                'Interactions',
                '[ReelsViewer] Error in reaction press',
                (e as any)?.message,
            )
        }
    }

    const handleReactionLongPress = (item: any) => {
        try {
            debug(
                'Interactions',
                '[ReelsViewer] Reaction long press - showing picker',
            )

            setShowReactionPicker(true)
            setSelectedPostForReaction(item)
        } catch (e) {
            debug(
                'Interactions',
                '[ReelsViewer] Error in reaction long press',
                (e as any)?.message,
            )
        }
    }

    const handleReactionSelect = async (item: any, reaction: any) => {
        try {
            if (!reaction || !item) return

            // 1. Toggle directo con el store
            const { current, previous } = toggleReaction(
                String(item._id),
                reaction._id || reaction.id,
                reactions,
            )
            console.log(
                'REACCIONES AL SELECCIONAR ==>',
                JSON.stringify({ current, previous }),
            )
            setShowReactionPicker(false)
            const authToken = await getAuthToken()
            if (!authToken) {
                toggleReaction(String(item._id), reaction._id, reactions)
                return
            }
            console.log('CUrrent ===>', JSON.stringify(current))
            let reactionID = current?.user_reaction?.[0].reaction_id ?? ''
            let data = await request_add_like_wall({
                authToken,
                post_id: item._id,
                reaction_id: reactionID,
            })
        } catch (e) {
            setShowReactionPicker(false)
        }
    }

    const getUserCurrentReaction = (item: any) => {
        try {
            const postId = String(item._id)
            const storeInteractions =
                useInteractionsStore.getState().interactions?.[postId]
            const storeUserReaction = storeInteractions?.user_reaction

            if (Array.isArray(storeUserReaction) && storeUserReaction.length > 0) {
                return storeUserReaction[0]
            }

            const itemUserReaction =
                item?.interactions?.user_reaction || item?.user_reaction
            if (Array.isArray(itemUserReaction) && itemUserReaction.length > 0) {
                return itemUserReaction[0]
            }

            return null
        } catch {
            return null
        }
    }

    // Filtrar solo posts que tengan al menos un media de tipo video
    const videoPosts: PostMinimal[] = useMemo(() => {
        const src = (postsContext as any[]) || []
        return src.filter(
            (p) =>
                Array.isArray(p?.medias) &&
                p.medias.some(
                    (m) =>
                        (m?.media_type || m?.content_type || '').toLowerCase() ===
                        'video',
                ),
        )
    }, [postsContext])

    // Cargar interacciones iniciales de los posts al store
    useEffect(() => {
        if (videoPosts.length > 0) {
            debug(
                'Interactions',
                '[ReelsViewer] Processing videoPosts for initial interactions',
                videoPosts.length,
            )
            const initialInteractions: Record<string, any> = {}

            videoPosts.forEach((post: any, index: number) => {
                const postId = String(post._id)
                debug('Interactions', `[ReelsViewer] Post ${index} structure:`, {
                    id: post._id,
                    hasInteractions: !!post.interactions,
                    interactionsKeys: post.interactions
                        ? Object.keys(post.interactions)
                        : [],
                    reactions: post.reactions || post.reaction_counter,
                    liked: post.liked,
                    user_reaction: post.user_reaction,
                    comment_count: post.comment_count || post.comments_counter,
                    favourite: post.favourite || post.status_favourite,
                })

                // Solo agregar si no existe ya en el store
                const existingInteraction =
                    useInteractionsStore.getState().interactions?.[postId]

                if (!existingInteraction) {
                    // Crear estructura de interacciones basada en los datos disponibles del post
                    const interactions: any = {
                        post_id: postId,
                    }

                    // Si hay post.interactions, usarlas
                    if (post.interactions) {
                        Object.assign(interactions, post.interactions)
                    }

                    // También agregar campos directos del post como fallback
                    if (post.reactions || post.reaction_counter) {
                        interactions.reactions =
                            post.reactions || post.reaction_counter
                    }
                    if (post.user_reaction) {
                        interactions.user_reaction = post.user_reaction
                    }
                    if (post.liked !== undefined) {
                        interactions.liked = post.liked
                    }
                    if (post.comment_count || post.comments_counter) {
                        interactions.comment_count =
                            post.comment_count || post.comments_counter
                    }
                    if (
                        post.favourite !== undefined ||
                        post.status_favourite !== undefined
                    ) {
                        interactions.favourite =
                            post.favourite || post.status_favourite
                        interactions.status_favourite =
                            post.favourite || post.status_favourite
                    }

                    // Cargar estado actual del usuario desde el backend (async)
                    const loadUserData = async () => {
                        try {
                            const {
                                PostsServices,
                            } = require('../../../database/posts.database')

                            // Obtener reacción actual del usuario para este post
                            const userReaction =
                                await PostsServices.getUserReaction(postId)
                            if (userReaction) {
                                const currentInteraction =
                                    useInteractionsStore.getState().interactions?.[
                                        postId
                                    ] || {}
                                setInteractions({
                                    [postId]: {
                                        ...currentInteraction,
                                        user_reaction: Array.isArray(userReaction)
                                            ? userReaction
                                            : [userReaction],
                                    },
                                })
                            }

                            // Obtener estado de favorito actual
                            const favoriteStatus =
                                await PostsServices.getUserFavoriteStatus(postId)
                            if (favoriteStatus !== undefined) {
                                const currentInteraction =
                                    useInteractionsStore.getState().interactions?.[
                                        postId
                                    ] || {}
                                setInteractions({
                                    [postId]: {
                                        ...currentInteraction,
                                        favourite: favoriteStatus ? 1 : 0,
                                        status_favourite: favoriteStatus ? 1 : 0,
                                    },
                                })
                            }
                        } catch (userDataError) {
                            debug(
                                'Interactions',
                                `[ReelsViewer] Error loading user data for ${postId}: ${(userDataError as any)?.message}`,
                            )
                        }
                    }

                    // Ejecutar carga de datos de usuario
                    loadUserData()

                    initialInteractions[postId] = interactions
                    debug(
                        'Interactions',
                        `[ReelsViewer] Created interaction for post ${postId}:`,
                        interactions,
                    )
                }
            })

            if (Object.keys(initialInteractions).length > 0) {
                setInteractions(initialInteractions)
                debug(
                    'Interactions',
                    '[ReelsViewer] Loaded initial interactions',
                    Object.keys(initialInteractions).length,
                )
            } else {
                debug(
                    'Interactions',
                    '[ReelsViewer] No initial interactions to load',
                )
            }
        }
    }, [videoPosts])

    // Índice inicial basado en post original
    const initialIndex = useMemo(() => {
        if (!initialPostId) return 0
        const idx = videoPosts.findIndex(
            (p) => p._id?.toString() === initialPostId.toString(),
        )
        return idx >= 0 ? idx : 0
    }, [initialPostId, videoPosts])

    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    // Ref con el valor "vivo" de currentIndex, para leer dentro de callbacks async
    // (setTimeout) que closurean sobre un currentIndex ya desactualizado.
    const currentIndexRef = useRef(currentIndex)
    useEffect(() => {
        currentIndexRef.current = currentIndex
    }, [currentIndex])

    // Sincronizar interacciones cuando cambia el video actual
    useEffect(() => {
        const currentPost = videoPosts[currentIndex]
        if (currentPost) {
            debug(
                'Interactions',
                `[ReelsViewer] Current video changed to index ${currentIndex}, post ${currentPost._id}`,
            )
            // Forzar re-render de las interacciones para el post actual
            // Esto asegura que se muestren los estados más actualizados
        }
    }, [currentIndex, videoPosts])

    const syncingFromExternalRef = useRef(false)
    const scrollInFlightRef = useRef(false)
    const viewedSetRef = useRef<Set<string | number>>(new Set())
    const viewTimersRef = useRef<Map<string | number, any>>(new Map())
    const currentVisibleIdRef = useRef<string | number | null>(null)
    const listReadyRef = useRef(false)
    const panY = useRef(new Animated.Value(0)).current
    const panYValueRef = useRef(0)
    const userGestureAutoplayRef = useRef(false)

    useEffect(() => {
        const id = panY.addListener((v) => {
            panYValueRef.current = v.value
        })
        return () => {
            panY.removeListener(id)
        }
    }, [panY])

    // Cargar preferencia de autoplay (por defecto activado)
    // useEffect(() => {
    //     ;(async () => {
    //         try {
    //             const v = await AsyncStorage.getItem('reels_autoplay_enabled')
    //             // Si nunca se ha guardado (null/undefined) o está explícitamente en 'true', activar
    //             // Solo desactivar si está explícitamente en 'false'
    //             setAutoplayEnabled(v !== 'false')
    //         } catch {}
    //     })()
    // }, [])

    const showToast = (msg: string) => {
        try {
            setToastMsg(msg)
            toastOpacity.setValue(0)
            Animated.timing(toastOpacity, {
                toValue: 1,
                duration: 160,
                useNativeDriver: true,
            }).start(() => {
                setTimeout(() => {
                    Animated.timing(toastOpacity, {
                        toValue: 0,
                        duration: 180,
                        useNativeDriver: true,
                    }).start()
                }, 1300)
            })
        } catch {}
    }

    const toggleAutoplay = async () => {
        const next = !autoplayEnabled
        setAutoplayEnabled(next)
        try {
            await AsyncStorage.setItem(
                'reels_autoplay_enabled',
                next ? 'true' : 'false',
            )
        } catch {}
        showToast(
            next
                ? 'Reproducción automática activada'
                : 'Reproducción automática desactivada',
        )
    }

    // Autoplay: solo el item visible
    const viewabilityConfig = useRef({ viewAreaCoveragePercentThreshold: 80 })
    const onViewableItemsChanged = useRef(({ viewableItems }: any) => {
        if (viewableItems && viewableItems.length > 0) {
            const v = viewableItems[0]
            if (typeof v.index === 'number') {
                debug(
                    'Video',
                    `[ReelsViewer] Viewable item changed to index: ${v.index}`,
                )
                setCurrentIndex(v.index)
                const postId = videoPosts[v.index]?._id
                currentVisibleIdRef.current = postId

                // Iniciar timer de vista (3s)
                if (postId != null && !viewTimersRef.current.has(postId)) {
                    const timer = setTimeout(async () => {
                        if (
                            currentVisibleIdRef.current === postId &&
                            !viewedSetRef.current.has(postId)
                        ) {
                            try {
                                const {
                                    PostsServices,
                                } = require('../../../database/posts.database')
                                viewedSetRef.current.add(postId)
                                debug('Views', '[ReelsViewer] addViewedPost', postId)
                                await PostsServices.addViewedPost(postId)
                            } catch (e) {
                                warn(
                                    'Views',
                                    '[ReelsViewer] addViewedPost error',
                                    (e as any)?.message,
                                )
                            }
                        }
                        viewTimersRef.current.delete(postId)
                    }, 3000)
                    viewTimersRef.current.set(postId, timer)
                }
                // Limpiar timers de otros
                for (const [pid, t] of viewTimersRef.current.entries()) {
                    if (pid !== postId) {
                        clearTimeout(t)
                        viewTimersRef.current.delete(pid)
                    }
                }
            }
        }
    })

    useEffect(() => {
        // Scroll inicial si no arranca en 0
        if (initialIndex > 0) {
            try {
                flatRef.current?.scrollToIndex({
                    index: initialIndex,
                    animated: false,
                })
            } catch {}
        }
    }, [initialIndex])

    const getItemLayout = useCallback(
        (_: any, index: number) => ({
            length: height,
            offset: height * index,
            index,
        }),
        [height],
    )

    const handleClose = () => {
        // Al cerrar queremos volver al feed y mantener la posición del primer post que abrió el modo fullscreen
        try {
            const goToCtx = (
                require('../appBar/context/GoToContext') as any
            ).getGoTo()
            const currentId = videoPosts[currentIndex]?._id ?? initialPostId
            goToCtx.setPostID(currentId) // feed hará scrollToPost al actual
        } catch {}

        // Sincronizar cambios del store al contexto antes de cerrar para que sincronice con el feed del muro
        try {
            const storeState = useInteractionsStore.getState().interactions
            videoPosts.forEach((post: any) => {
                const postId = String(post._id)
                const storeData = storeState?.[postId]

                // Si hay cambios de favorito en el store, actualizar el contexto
                if (
                    storeData?.favourite !== undefined ||
                    storeData?.status_favourite !== undefined
                ) {
                    const favoriteValue =
                        storeData.favourite ?? storeData.status_favourite
                    updateFavouritePostsContext(postId, favoriteValue)
                }
            })
            debug(
                'Interactions',
                '[ReelsViewer] Synced store changes to context on close',
            )
        } catch (e) {
            debug(
                'Interactions',
                `[ReelsViewer] Error syncing to context: ${(e as any)?.message}`,
            )
        }

        navigation.goBack()
    }

    const closeWithFade = () => {
        try {
            Animated.timing(fadeAnim, {
                toValue: 0,
                duration: 180,
                useNativeDriver: true,
            }).start(() => {
                handleClose()
                // Restaurar para próximas aperturas
                fadeAnim.setValue(1)
            })
        } catch {
            handleClose()
        }
    }

    const goToIndex = (idx: number) => {
        if (idx < 0 || idx >= videoPosts.length) {
            debug(
                'Video',
                `[ReelsViewer] goToIndex ignored, out of range: ${idx}/${videoPosts.length - 1}`,
            )
            return
        }
        // Evitar llamadas concurrentes a scrollToIndex: en Android, dos scrolls
        // programáticos superpuestos sobre el mismo FlatList pueden crashear la app.
        // Cualquier fuente (auto-advance, timer de fin, sync externo) pasa por acá.
        if (scrollInFlightRef.current) {
            debug('Video', `[ReelsViewer] goToIndex ignored, scroll in flight`)
            return
        }
        scrollInFlightRef.current = true
        setTimeout(() => {
            scrollInFlightRef.current = false
        }, 450)
        debug(
            'Video',
            `[ReelsViewer] goToIndex called: ${idx}/${videoPosts.length - 1}`,
        )
        try {
            flatRef.current?.scrollToIndex({ index: idx, animated: true })
            debug(
                'Video',
                `[ReelsViewer] ScrollToIndex executed successfully for index ${idx}`,
            )
        } catch (e) {
            debug(
                'Video',
                `[ReelsViewer] ScrollToIndex failed: ${(e as any)?.message}`,
            )
        }
        // Tras navegar programáticamente, forzar play si ya hay consentimiento
        if (autoplayEnabled && userGestureAutoplayRef.current) {
            const media = require('../../utils/mediaSingleton').default
            const pid = videoPosts[idx]?._id
            if (pid != null) {
                debug('Video', `[ReelsViewer] Scheduling media play for post ${pid}`)
                setTimeout(() => {
                    try {
                        media.playCurrentAudioById(pid)
                    } catch {}
                }, 360)
            }
        }
    }

    // Ventana de gracia tras montar: ignoramos sync externo de mediaSingleton mientras
    // el feed anterior puede seguir "vivo" (transición de pantalla en Android es más
    // lenta) y disparar un notifyPlaying tardío de un post distinto al que se abrió,
    // secuestrando la navegación recién entrada a Reels.
    const externalSyncReadyRef = useRef(false)
    useEffect(() => {
        externalSyncReadyRef.current = false
        const t = setTimeout(() => {
            externalSyncReadyRef.current = true
        }, 800)
        return () => clearTimeout(t)
    }, [])

    // Sincronización con cambios externos (main feed) usando mediaSingleton
    useEffect(() => {
        const media = require('../../utils/mediaSingleton').default
        const handler = (postId: any) => {
            if (!isFocused) return
            if (!externalSyncReadyRef.current) return
            // Evitar reentrancia: si ya hay un sync externo en curso, ignorar
            // eventos adicionales en ráfaga (varios notifyPlaying casi simultáneos
            // pueden disparar múltiples scrollToIndex concurrentes y crashear en Android).
            if (syncingFromExternalRef.current) return
            if (postId == null) return
            // Buscar índice de ese post en la lista de videos
            const idx = videoPosts.findIndex(
                (p) => p._id?.toString() === String(postId),
            )
            if (idx < 0) return
            if (idx === currentIndex) return
            syncingFromExternalRef.current = true
            goToIndex(idx)
            // Al terminar la animación de scroll, apagamos el flag (timeout aproximado)
            setTimeout(() => {
                syncingFromExternalRef.current = false
            }, 400)
        }
        try {
            media.subscribeVideo(handler)
        } catch {}
        return () => {
            try {
                media.unsubscribeVideo(handler)
            } catch {}
        }
    }, [isFocused, videoPosts, currentIndex])

    const handlePlaybackStatusUpdate = (indexItem: number) => (status: any) => {
        if (!status?.isLoaded) return
        const duration = status.durationMillis || 0
        const position = status.positionMillis || 0
        const remainingMs = Math.max(0, duration - position)
        const remainingSec = Math.ceil(remainingMs / 1000)

        // Autoplay directo sin overlay "Up Next" - programar avance automático al final.
        // MIN_RELIABLE_DURATION_MS: en Android, videos pesados pueden reportar un
        // durationMillis parcial/chico (basado en lo poco que llevan buffereado) antes
        // de resolver la duración real. Sin este piso, esa duración falsa hace
        // remainingSec <= 1 casi al instante y el video se saltea antes de arrancar.
        // El fallback normal (didJustFinish) sigue cubriendo el fin real del video.
        const MIN_RELIABLE_DURATION_MS = 2000
        if (
            indexItem === currentIndex &&
            duration > MIN_RELIABLE_DURATION_MS &&
            autoplayEnabled
        ) {
            const next = indexItem + 1
            if (remainingSec <= 1 && next < videoPosts.length) {
                // Programar avance directo cuando queda 1 segundo o menos
                if (!upNextTimerRef.current) {
                    const ms = Math.max(100, remainingSec * 1000)
                    debug(
                        'Video',
                        `[ReelsViewer] Setting direct advance timer for ${ms}ms from ${indexItem} to ${next}`,
                    )
                    upNextTimerRef.current = setTimeout(() => {
                        upNextTimerRef.current = null
                        // Usamos el ref "vivo": el currentIndex capturado en este closure
                        // ya vale indexItem por construcción (arriba), así que compararlo
                        // consigo mismo sería siempre true y no protegería contra nada.
                        if (
                            currentIndexRef.current === indexItem &&
                            next < videoPosts.length &&
                            autoplayEnabled
                        ) {
                            debug(
                                'Video',
                                `[ReelsViewer] Direct advance timer triggered, going from ${indexItem} to ${next}`,
                            )
                            goToIndex(next)
                        }
                    }, ms)
                }
            }
        }

        if (status?.didJustFinish) {
            // Ignorar eventos de finalización de items que no son el visible actual.
            // En Android, videos montados fuera de pantalla (windowSize) pueden seguir
            // reproduciendo brevemente por el delay async de pauseAsync() y disparar
            // didJustFinish, lo que forzaría un salto de índice incorrecto.
            if (indexItem !== currentIndex) return
            const next = indexItem + 1
            debug(
                'Video',
                `[ReelsViewer] Video ${indexItem} finished. Autoplay: ${autoplayEnabled}, Next: ${next}/${videoPosts.length}`,
            )

            // Limpiar cualquier timer pendiente
            if (upNextTimerRef.current) {
                clearTimeout(upNextTimerRef.current)
                upNextTimerRef.current = null
            }

            // Solo avanzar automáticamente si el autoplay está activado
            if (autoplayEnabled) {
                if (next < videoPosts.length) {
                    debug(
                        'Video',
                        `[ReelsViewer] Auto-advancing from video ${indexItem} to ${next}`,
                    )
                    // Avance directo inmediato
                    goToIndex(next)
                } else {
                    // Último: cerrar con fade
                    debug(
                        'Video',
                        '[ReelsViewer] Reached last video, closing with fade',
                    )
                    closeWithFade()
                }
            } else {
                debug(
                    'Video',
                    '[ReelsViewer] Autoplay disabled, staying on current video',
                )
            }
        }
    }

    const handleUpNextPress = () => {
        if (upNext.nextIndex != null) {
            goToIndex(upNext.nextIndex)
            setUpNext({ visible: false, seconds: 0, nextIndex: null })
        } else {
            closeWithFade()
        }
    }

    const handleUpNextPlayNow = () => {
        if (upNext.nextIndex != null) {
            const nextIdx = upNext.nextIndex
            const nextId = (videoPosts[nextIdx] as any)?._id
            setUpNext({ visible: false, seconds: 0, nextIndex: null })
            goToIndex(nextIdx)
            // Forzar reproducción del siguiente sólo por esta acción, sin cambiar el toggle global
            setTimeout(() => {
                try {
                    const media = require('../../utils/mediaSingleton').default
                    if (nextId != null) media.playCurrentAudioById(nextId)
                } catch {}
            }, 380)
        } else {
            closeWithFade()
        }
    }

    // Nuevo cierre por "pull-down" usando overscroll negativo del FlatList (sólo primer item).
    // Evita interceptar gestos normales de navegación entre videos.
    const CLOSE_THRESHOLD = 130
    const handleScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            const y = e.nativeEvent.contentOffset.y
            if (currentIndex === 0 && y < 0) {
                // y es negativo durante overscroll; usamos su magnitud para animar.
                const pull = Math.min(220, -y) // cap para no exagerar
                panY.setValue(pull)
            } else if (panYValueRef.current !== 0 && y >= 0) {
                panY.setValue(0)
            }
        },
        [currentIndex, panY],
    )

    const handleScrollEnd = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            const y = e.nativeEvent.contentOffset.y
            if (currentIndex === 0 && y < -CLOSE_THRESHOLD) {
                // Cerrar
                Animated.timing(panY, {
                    toValue: height,
                    duration: 170,
                    useNativeDriver: true,
                }).start(handleClose)
            } else if (panYValueRef.current !== 0) {
                Animated.spring(panY, {
                    toValue: 0,
                    useNativeDriver: true,
                    bounciness: 0,
                })
            }
            // Forzar reproducción del visible si ya hubo gesto de usuario
            try {
                if (
                    autoplayEnabled &&
                    userGestureAutoplayRef.current &&
                    currentVisibleIdRef.current != null
                ) {
                    const media = require('../../utils/mediaSingleton').default
                    media.playCurrentAudioById(currentVisibleIdRef.current)
                }
            } catch {}
        },
        [currentIndex, height, panY],
    )

    const translateStyle = { transform: [{ translateY: panY }] }

    // Ocultar overlay Up Next al cambiar de video visible
    useEffect(() => {
        if (upNext.visible)
            setUpNext({ visible: false, seconds: 0, nextIndex: null })
    }, [currentIndex])

    // Publicar el post visible actual para que el feed principal salte a ese al cerrar (y opcionalmente sincronizar ya)
    useEffect(() => {
        try {
            const goToCtx = (
                require('../appBar/context/GoToContext') as any
            ).getGoTo()
            const id = videoPosts[currentIndex]?._id
            if (id != null) goToCtx.setPostID(id)
        } catch {}
    }, [currentIndex, videoPosts])
    const parentVideoRef = useRef<Record<string, Video>>({})

    useEffect(() => {
        if (Object.keys(parentVideoRef.current).length === 0) return
        if (showCommentsModal === true || showReactionPicker === true) {
            playOrStopVideo(false)
        } else {
            playOrStopVideo(true)
        }
    }, [showCommentsModal, showReactionPicker])

    async function playOrStopVideo(shouldPlay: boolean) {
        if (!shouldPlay) {
            Object.values(parentVideoRef.current).forEach(async (videoRef) => {
                try {
                    await videoRef.pauseAsync()
                } catch (e) {}
            })
        } else {
            // 2. Darle play al video que el usuario está viendo
            const visibleItemID = videoPosts[currentIndex]?._id ?? 0
            const visibleVideo = parentVideoRef.current[visibleItemID]

            if (visibleVideo) {
                await visibleVideo.playAsync()
            }
        }
    }

    const [manuallyMuted, wasManuallyMuted] = useState(false)
    async function volumeVideo(mute: boolean) {
        Object.values(parentVideoRef.current).forEach(async (videoRef) => {
            try {
                await videoRef.setVolumeAsync(mute ? 0 : 1)
            } catch (e) {}
        })
    }

    const renderItem = ({ item, index }) => (
        <ReelItem
            item={item}
            index={index}
            currentIndex={currentIndex} // Estado de tu pantalla
            autoplayEnabled={autoplayEnabled} // Estado de tu pantalla
            userGestureAutoplayRef={userGestureAutoplayRef} // Tu ref local de la pantalla
            width={width}
            height={height}
            handleClose={handleClose}
            handlePlaybackStatusUpdate={handlePlaybackStatusUpdate}
            handleReactionPress={handleReactionPress}
            handleReactionLongPress={handleReactionLongPress}
            handlePresentCommentsModal={handlePresentCommentsModal}
            handleFavoritePress={handleFavoritePress}
            getUserReactionIcon={getUserReactionIcon}
            getReactionCount={getReactionCount}
            getCommentCount={getCommentCount}
            getUserFavoritePost={getUserFavoritePost}
            getFavoriteCount={getFavoriteCount}
            onVideoRef={(ref) => {
                if (ref) {
                    // 1. Nos fijamos si este video ya estaba registrado antes
                    const isNewVideo = !parentVideoRef.current[item._id]

                    // 2. Guardamos la referencia
                    parentVideoRef.current[item._id] = ref

                    // 3. Si es nuevo, lo forzamos a respetar el estado global de mute
                    if (isNewVideo) {
                        try {
                            console.log(
                                'LLAMO A mUTEAR CON NUEVO VIDEO ==>',
                                manuallyMuted,
                            )
                            volumeVideo(manuallyMuted)
                        } catch (e) {
                            // Atrapamos silenciosamente por si el ref nativo aún no está 100% listo
                        }
                    }
                }
            }}
            wasManuallyMuted={(v) => {
                wasManuallyMuted(v)
                if (parentVideoRef?.current) {
                    volumeVideo(v)
                }
            }}
            manuallyMuted={manuallyMuted}
        />
    )

    return (
        <Animated.View
            style={[
                { flex: 1, backgroundColor: 'black', opacity: fadeAnim },
                translateStyle,
            ]}
        >
            <StatusBar barStyle='light-content' hidden={false} />
            {videoPosts.length === 0 ? (
                <View
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Text style={{ color: 'white' }}>No hay videos</Text>
                </View>
            ) : (
                <FlatList
                    ref={flatRef}
                    data={videoPosts}
                    keyExtractor={(p) => p._id.toString()}
                    renderItem={renderItem}
                    pagingEnabled
                    showsVerticalScrollIndicator={false}
                    snapToAlignment='start'
                    decelerationRate='fast'
                    onViewableItemsChanged={onViewableItemsChanged.current}
                    viewabilityConfig={viewabilityConfig.current}
                    getItemLayout={getItemLayout}
                    windowSize={4}
                    initialNumToRender={4}
                    maxToRenderPerBatch={4}
                    removeClippedSubviews
                    bounces
                    scrollEventThrottle={16}
                    onScroll={handleScroll}
                    onScrollEndDrag={handleScrollEnd}
                    onMomentumScrollEnd={handleScrollEnd}
                />
            )}

            {/* Toast */}
            {toastMsg ? (
                <Animated.View
                    style={{
                        position: 'absolute',
                        bottom: 70,
                        alignSelf: 'center',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 12,
                        opacity: toastOpacity,
                    }}
                >
                    <Text style={{ color: 'white', fontSize: 12 }}>{toastMsg}</Text>
                </Animated.View>
            ) : null}

            {/* Modal de comentarios */}
            <Modal
                visible={showCommentsModal}
                transparent={true}
                animationType='fade'
                onRequestClose={handleCloseCommentsModal}
            >
                <SafeKeyboardControllerAvoidingView //Modal sale del flujo principal de pantallas, por lo que algo como KeyboardAvoidingView no funciona por default aca
                    style={{ flex: 1 }}
                    behavior='padding'
                >
                    <View
                        style={{
                            flex: 1,
                            backgroundColor: 'rgba(0,0,0,0.85)',
                            paddingTop: Platform.OS === 'web' ? 20 : 48,
                        }}
                    >
                        {/* Botón cerrar */}
                        <TouchableOpacity
                            onPress={handleCloseCommentsModal}
                            style={{
                                position: 'absolute',
                                top: Platform.OS === 'web' ? 10 : 50,
                                right: 14,
                                zIndex: 1000,
                                padding: 8,
                            }}
                        >
                            <Ionicons name='close' size={28} color='#fff' />
                        </TouchableOpacity>

                        <View
                            style={{
                                flex: 1,
                                backgroundColor: 'white',
                                marginTop: 40,
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                overflow: 'hidden',
                            }}
                        >
                            {commentsPost && (
                                <PostWithComments
                                    post={commentsPost}
                                    closeSheet={handleCloseCommentsModal}
                                    onPostUpdate={(updatedInteractions) => {
                                        // merge y propagar
                                        const newInter = {
                                            post_id: commentsPost._id,
                                            ...commentsPost.interactions,
                                            ...updatedInteractions,
                                        }
                                        handleUpdateInteractions(newInter)
                                    }}
                                    commentsOnly={true}
                                />
                            )}
                            <SafeBottomSpace openSpace={16} />
                        </View>
                    </View>
                </SafeKeyboardControllerAvoidingView>
            </Modal>

            {/* Modal de Picker de Reacciones */}
            <Modal
                visible={showReactionPicker}
                transparent={true}
                animationType='fade'
                onRequestClose={() => setShowReactionPicker(false)}
            >
                <View style={styles.reactionPickerOverlay}>
                    <TouchableOpacity
                        style={styles.reactionPickerBackdrop}
                        activeOpacity={1}
                        onPress={() => setShowReactionPicker(false)}
                    />
                    <View style={styles.reactionPickerContainer}>
                        <Text style={styles.reactionPickerTitle}>
                            Elige una reacción
                        </Text>
                        <View style={styles.reactionsList}>
                            {reactions
                                .sort((a, b) => a.index - b.index)
                                .map((reaction, index) => (
                                    <TouchableOpacity
                                        key={reaction._id || reaction.id || index}
                                        style={styles.reactionItem}
                                        onPress={() =>
                                            handleReactionSelect(
                                                selectedPostForReaction,
                                                reaction,
                                            )
                                        }
                                    >
                                        <Image
                                            source={getReactionAsset(reaction?.name)}
                                            style={
                                                reaction.isSelected
                                                    ? {
                                                          width: 28,
                                                          height: 28,
                                                          marginRight: 6,
                                                      }
                                                    : {
                                                          width: 24,
                                                          height: 24,
                                                          marginRight: 6,
                                                      }
                                            }
                                        />
                                    </TouchableOpacity>
                                ))}
                        </View>
                    </View>
                </View>
            </Modal>
        </Animated.View>
    )
}
const RenderButtokLike = () => {
    return <></>
}
const styles = StyleSheet.create({
    overlayTop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 80,
        paddingTop: Platform.OS === 'web' ? 34 : 50,
        paddingHorizontal: 12,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        zIndex: 100,
    },
    closeBtn: { backgroundColor: 'rgba(0,0,0,0.45)', padding: 8, borderRadius: 24 },
    overlayRight: {
        position: 'absolute',
        right: 12,
        bottom: 110,
        alignItems: 'center',
        zIndex: 100,
    },
    fab: {
        backgroundColor: 'rgba(0,0,0,0.45)',
        padding: 12,
        borderRadius: 32,
        marginVertical: 10,
        alignItems: 'center',
    },
    fabCount: {
        color: 'white',
        fontSize: 10,
        fontWeight: '600',
        marginTop: 2,
        textAlign: 'center',
    },
    metaContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50,
    },
    metaGradient: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 250,
    },
    metaBottom: {
        paddingHorizontal: 12,
        paddingBottom: 30,
        paddingTop: 10,
        paddingRight: 90,
    },
    postTitle: { color: 'white', fontSize: 17, fontWeight: '700', marginBottom: 4 },
    postDesc: { color: '#f1f1f1', fontSize: 13, lineHeight: 17, marginBottom: 8 },
    lastCommentBox: {
        backgroundColor: 'rgba(255,255,255,0.06)',
        padding: 10,
        borderRadius: 12,
    },
    lastCommentUser: {
        color: theme.colors?.colorPrimary || '#4da6ff',
        fontSize: 12,
        fontWeight: '600',
    },
    lastCommentText: { color: 'white', fontSize: 12, marginTop: 2 },
    // Up Next overlay
    upNextOverlay: { position: 'absolute', right: 12, bottom: 28, left: 12 },
    upNextBox: {
        marginLeft: 'auto',
        backgroundColor: 'rgba(0,0,0,0.55)',
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 12,
        maxWidth: 320,
    },
    upNextTitle: { color: 'white', fontWeight: '800', fontSize: 14 },
    upNextSubtitle: { color: '#f1f1f1', fontSize: 12, marginTop: 2 },
    upNextCountdownRow: {
        flexDirection: 'row-reverse',
        alignItems: 'flex-end',
        marginTop: 6,
    },
    upNextCountdownNum: {
        color: 'white',
        fontSize: 24,
        fontWeight: '900',
        marginLeft: 8,
    },
    upNextCountdownText: { color: '#f1f1f1', fontSize: 12 },
    upNextCta: { color: '#eaeaea', fontSize: 11, marginTop: 6, textAlign: 'right' },
    upNextBtn: {
        marginTop: 8,
        alignSelf: 'flex-end',
        backgroundColor: 'rgba(255,255,255,0.2)',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 8,
    },
    upNextBtnText: { color: 'white', fontSize: 12, fontWeight: '700' },
    // Reaction Picker styles
    reactionPickerOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.7)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    reactionPickerBackdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    reactionPickerContainer: {
        backgroundColor: theme.colors?.background || 'white',
        borderRadius: 16,
        padding: 20,
        margin: 20,
        maxWidth: 320,
        width: '90%',
    },
    reactionPickerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#333',
        textAlign: 'center',
        marginBottom: 16,
    },
    reactionsList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    reactionItem: {
        alignItems: 'center',
        justifyContent: 'center',
        width: 80,
        height: 80,
        margin: 8,
        borderRadius: 12,
        backgroundColor: '#f8f8f8',
    },
    reactionEmoji: { fontSize: 32, marginBottom: 4 },
    reactionName: {
        fontSize: 11,
        fontWeight: '600',
        color: '#333',
        textAlign: 'center',
    },
})

interface ReelItemProps {
    item: any
    index: number
    currentIndex: number
    autoplayEnabled: boolean
    userGestureAutoplayRef: React.MutableRefObject<boolean>
    width: number
    height: number
    handleClose: () => void
    handlePlaybackStatusUpdate: (index: number) => (status: any) => void
    handleReactionPress: (item: any) => void
    handleReactionLongPress: (item: any) => void
    handlePresentCommentsModal: (item: any) => void
    handleFavoritePress: (id: string) => void
    getUserReactionIcon: (item: any) => React.ReactNode
    getReactionCount: (item: any) => number
    getCommentCount: (item: any) => number
    getUserFavoritePost: (item: any) => boolean
    getFavoriteCount: (item: any) => number
    onVideoRef: (ref: any) => void
    wasManuallyMuted?: (v: boolean) => void
    manuallyMuted?: boolean
}

const ReelItemBase = ({
    item,
    index,
    currentIndex,
    autoplayEnabled,
    userGestureAutoplayRef,
    width,
    height,
    handleClose,
    handlePlaybackStatusUpdate,
    handleReactionPress,
    handleReactionLongPress,
    handlePresentCommentsModal,
    handleFavoritePress,
    getUserReactionIcon,
    getReactionCount,
    getCommentCount,
    getUserFavoritePost,
    getFavoriteCount,
    onVideoRef,
    wasManuallyMuted,
    manuallyMuted,
}: ReelItemProps) => {
    const media =
        (item.medias || []).find(
            (m: any) =>
                (m?.media_type || m?.content_type || '').toLowerCase() === 'video',
        ) || (item.medias || [])[0]
    const uri = media?.media_url || media?.media
    const [natural, setNatural] = useState<{
        width: number
        height: number
        ratio: number
    } | null>(null)

    // Obtener datos actualizados del store
    const storeInteractions = useInteractionsStore(
        useCallback((state) => state.interactions?.[String(item._id)], [item._id]),
    )
    const effectiveItem = { ...item, ...storeInteractions }

    // Definimos un umbral un poco más estricto para considerar "portrait" (muy alto) y así centrar adecuadamente cuadrados y horizontales
    const portrait = natural ? natural.ratio < 0.8 : false
    // Centramos horizontal y verticalmente cuando NO es portrait para evitar que quede pegado arriba (barras negras simétricas).
    const videoWrapperStyle: any = portrait
        ? { flex: 1 }
        : { flex: 1, justifyContent: 'center', alignItems: 'center' }
    const lastComment =
        Array.isArray(item?.comments) && item.comments.length > 0
            ? item.comments[item.comments.length - 1]
            : null
    const showMeta = !!item?.title || !!item?.description_short || !!lastComment
    const [showMore, setShowMore] = useState(false)

    return (
        <View style={{ width, height, backgroundColor: 'black' }}>
            <View style={videoWrapperStyle}>
                <VideoPlayerAV
                    uri={uri}
                    onVideoRef={(ref) => onVideoRef(ref)}
                    autoplay={autoplayEnabled && currentIndex === index}
                    isVisible={currentIndex === index}
                    post_id={item._id}
                    isPodcast={false}
                    contentSeen={() => {}}
                    path={'post'}
                    containerWidth={width}
                    onReady={() => {}}
                    onPlaybackStatusUpdateExternal={handlePlaybackStatusUpdate(
                        index,
                    )}
                    onNaturalSize={(info: any) => setNatural(info)}
                    waitForNaturalSize
                    controlsMode='minimal'
                    allowAutoplayAfterUserGesture={
                        autoplayEnabled && userGestureAutoplayRef.current
                    }
                    onUserPlayIntent={() => {
                        userGestureAutoplayRef.current = true
                    }}
                    extraControls={true}
                    wasManuallyMuted={wasManuallyMuted}
                />
            </View>
            <View style={styles.overlayTop} pointerEvents='box-none'>
                <TouchableOpacity
                    onPress={handleClose}
                    style={styles.closeBtn}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                >
                    <Feather name='x' size={26} color='white' />
                </TouchableOpacity>
            </View>
            <View style={styles.overlayRight} pointerEvents='box-none'>
                {/* Toggle Autoplay */}
                {/* <TouchableOpacity style={styles.fab} onPress={toggleAutoplay}>
                        <Feather
                            name={autoplayEnabled ? 'play-circle' : 'pause-circle'}
                            size={22}
                            color='white'
                        />
                    </TouchableOpacity> */}

                {/* Botón de Reacciones con Picker */}
                <TouchableOpacity
                    style={styles.fab}
                    onPress={() => handleReactionPress(effectiveItem)}
                    onLongPress={() => handleReactionLongPress(effectiveItem)}
                    delayLongPress={500}
                >
                    {getUserReactionIcon(effectiveItem)}
                    {getReactionCount(effectiveItem) > 0 && (
                        <Text style={styles.fabCount}>
                            {getReactionCount(effectiveItem)}
                        </Text>
                    )}
                </TouchableOpacity>

                {/* Botón de Comentarios */}
                <TouchableOpacity
                    style={styles.fab}
                    onPress={() => handlePresentCommentsModal(effectiveItem)}
                >
                    <Feather name='message-circle' size={22} color='white' />
                    {getCommentCount(effectiveItem) > 0 && (
                        <Text style={styles.fabCount}>
                            {getCommentCount(effectiveItem)}
                        </Text>
                    )}
                </TouchableOpacity>

                {/* Botón de Favoritos */}
                <TouchableOpacity
                    style={styles.fab}
                    onPress={() => handleFavoritePress(effectiveItem?._id)}
                >
                    <Ionicons
                        name={
                            getUserFavoritePost(effectiveItem)
                                ? 'bookmark'
                                : 'bookmark-outline'
                        }
                        size={22}
                        color='white'
                        // weight={
                        //     getUserFavoritePost(effectiveItem)
                        //         ? 'fill'
                        //         : 'regular'
                        // }
                    />
                    {/* Para mantener el espacio aunque no esté activo */}
                    {getFavoriteCount(effectiveItem) > 0 && (
                        <Text style={styles.fabCount}>
                            {getFavoriteCount(effectiveItem)}
                        </Text>
                    )}
                </TouchableOpacity>
            </View>
            {showMeta && (
                <Pressable
                    style={styles.metaContainer}
                    pointerEvents='box-none'
                    onPress={() => setShowMore((v) => !v)}
                >
                    {/* Gradient para mejorar legibilidad del texto sobre cualquier frame del video */}
                    <LinearGradient
                        colors={[
                            'rgba(0,0,0,0.85)',
                            'rgba(0,0,0,0.85)',
                            'rgba(0,0,0,0.55)',
                            'rgba(0,0,0,0)',
                        ]}
                        start={{ x: 0.5, y: 1 }}
                        end={{ x: 0.5, y: 0 }}
                    >
                        <View style={[styles.metaBottom]}>
                            {!!item?.title && (
                                <Text
                                    style={styles.postTitle}
                                    numberOfLines={showMore ? 0 : 2}
                                >
                                    {item.title}
                                </Text>
                            )}
                            {!!item?.description_short && (
                                <Text
                                    style={styles.postDesc}
                                    numberOfLines={showMore ? 0 : 3}
                                >
                                    {item.description_short}
                                </Text>
                            )}
                            {lastComment && (
                                <View style={styles.lastCommentBox}>
                                    <Text
                                        style={styles.lastCommentUser}
                                        numberOfLines={1}
                                    >
                                        {lastComment?.user ||
                                            lastComment?.publisher ||
                                            'Usuario'}
                                    </Text>
                                    <Text
                                        style={styles.lastCommentText}
                                        numberOfLines={2}
                                    >
                                        {lastComment?.message || lastComment?.text}
                                    </Text>
                                </View>
                            )}
                        </View>
                    </LinearGradient>
                </Pressable>
            )}
        </View>
    )
}

const ReelItem = React.memo(ReelItemBase)
